import { Injector } from '@angular/core';
import { asyncScheduler, BehaviorSubject, Observable, of, from, Subscription } from 'rxjs';
import { catchError, mergeMap, tap } from 'rxjs/operators';
import { isEqual, some } from 'lodash';
import moment from 'moment-timezone';

import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { TagsService } from '../../../Common/Tags/services/tags.service';
import { TagGroupsService } from '../../../Common/Tags/services/tag-groups.service';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { MonitorConfigService } from '../../../Common/MonitorShared/services/monitor-config.service';
import { MentionSearchQueryModel } from '../../Shared/models/mention-search-query.model';
import { MentionSearchCriteriaModel } from '../../Shared/models/mention-search-criteria.model';
import { MentionsApi } from '../../../api/mentions/mentions.api';
import { MentionsSearchResponseModel } from '../../../api/mentions/model/mentions-search-response.model';
import { MentionSearchFacetTypesEnum } from '../../../api/mentions/enum/mention-search-facet-types.enum';
import { GeographyTypesEnum } from '../../../api/journodex/enum/geography-types.enum';
import { MentionSearchService } from './mention-search.service';
import { JournodexRegionsService } from '../../../Common/Journodex/services/journodex-regions.service';
import { SourceGroupService } from '../../../Common/Sources/services/source-group.service';
import { ReportsSeriesMapperService } from '../../../Common/Reports/services/reports-series-mapper.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { CurrencyConversionService } from '../../../Core/Users/services/currency-conversion.service';
import { SavedFilterService } from '@Common/Filter/services/saved-filter.service';
import { monitoringFiltersEnum } from '@Monitor/Shared/enums/monitoring-filters.enum';
import { HttpErrorResponse } from '@angular/common/http';
import { DateQueryTypeEnum } from '@api/mentions/enum/date-query-type.enum';
import { RelativeTimezoneDateTypeEnum } from '@api/mentions/enum/relative-timezone-date-type.enum';
import { DateFacetModel } from '@api/mentions/model/date-facet.model';
import { NeoFacetRequestApiModel } from '@api/mentions/model/neo-facet-request-api.model';
import { SearchCriteriaModel } from '@api/mentions/model/search-criteria.model';
import { InvalidFiltersError } from '@Monitor/Shared/errors/invalid-filters.error';
import { ReportSeriesMentionRequestGeneratorOutput } from '@Common/Reports/models/report-series-mention-request-generator-output.model';
import { IndexeddbCoreCacheService } from '@Core/Cache/services/indexeddb-core-cache.service';
import { DiscoverListsFilterService } from '@Common/Filter/services/discover-lists-filter.service';
import { ReportSeriesFacetGeneratorOutput } from '@Common/Reports/models/report-series-facet-generator-output.model';
import { ReportSeriesFacetGeneratorEnum } from '@Common/Reports/models/report-series-facet-generator.enum';
import { ReportSeriesModel } from '@Common/Reports/models/report-series.model';
import { DuplicatesStatusEnum } from '../../../Common/Filter/enums/duplicates-status.enum';

interface SearchCriteriaMapItem {
    request: SearchCriteriaModel;
    response: MentionsSearchResponseModel;
    mentionRequest?: ReportSeriesMentionRequestGeneratorOutput;
    series: ReportSeriesModel[];
}

export abstract class AbstractDashboardSearchService extends MentionSearchService {

    public searchCriteriaMap: Map<string, SearchCriteriaMapItem> = new Map();
    conflictingSavedFilters$:  BehaviorSubject<any | null> = new BehaviorSubject<any | null>(null);

    private sourceSeries: ReportSeriesModel;
    private sourceSeries$: BehaviorSubject<ReportSeriesModel> = new BehaviorSubject<ReportSeriesModel>(null);

    private fetchingData$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private pendingFetchCount = 0;

    constructor(
        protected tagsService: TagsService,
        protected tagGroupsService: TagGroupsService,
        protected journodexService: JournodexRegionsService,
        protected mentionsApi: MentionsApi,
        protected currentUserStateService: CurrentUserStateService,
        protected reportSeriesMapperService: ReportsSeriesMapperService,
        protected sourceGroupService: SourceGroupService,
        protected localizationService: LocalizationService,
        protected currencyConversionService: CurrencyConversionService,
        protected monitorConfigService: MonitorConfigService,
        protected savedFilterService: SavedFilterService,
        protected discoverListsFilterService: DiscoverListsFilterService,
        protected injector: Injector,
        protected cacheService: IndexeddbCoreCacheService,
    ) {
        super(
            tagsService,
            tagGroupsService,
            journodexService,
            mentionsApi,
            currentUserStateService,
            reportSeriesMapperService,
            sourceGroupService,
            localizationService,
            currencyConversionService,
            monitorConfigService,
            savedFilterService,
            discoverListsFilterService,
            injector,
        );
    }

    getSourceSeries(): Observable<ReportSeriesModel> {
        return this.sourceSeries$;
    }

    setSourceSeries(sourceSeries: ReportSeriesModel): void {
        this.sourceSeries = sourceSeries;
        this.sourceSeries$.next(this.sourceSeries);
    }

    /**
     * Main logical function, accepts arrays of series, determines the most efficient way to fetch data for that array, and eventually updates the dataProvider
     * property of the subject with that data
     *
     * Nothing needs to know when all the data has loaded for a dashboard, there are no save actions to consider
     */
    public fulfillSeries(series: ReportSeriesModel[], fetchData: boolean = true): void {
        // Populate a map of all the unique search criterion that need to be fulfilled, with the series that will rely on them
        for (const [index, singleSeries] of series.entries()) {

            if (!singleSeries) {
                continue;
            }

            // add previous series if it has previous period
            if (singleSeries.hasPreviousPeriod) {
                const previousPeriodSeries = singleSeries.clone();
                previousPeriodSeries.hasPreviousPeriod = true;
                previousPeriodSeries.mentions.isPreviousPeriod = true;
                previousPeriodSeries.previousPeriodDataProvider = singleSeries.previousPeriodDataProvider;

                const previousPeriodKey = previousPeriodSeries.getSeriesKey() + index;
                this.searchCriteriaMap.set(
                    previousPeriodKey,
                    {
                        request: previousPeriodSeries.toSearchCriteriaModel(),
                        response: null,
                        mentionRequest: singleSeries.mentions?.mentionRequestGenerator ? singleSeries.mentions?.mentionRequestGenerator() : null,
                        series: [previousPeriodSeries]
                    }
                );
            }

            if (singleSeries.mentions.dataMapper || singleSeries.mentions.mentionDataMapper || singleSeries.mentions.asyncDataMapper) {
                // Next null so the chart shows loading
                singleSeries.dataProvider.next(null);
            }
            if (singleSeries.mentionProvider) {
                singleSeries.mentionProvider.next(null);
            }

            /**
             * When we need to add it to a map, there are four scenarios:
             * 1. No series key exists, the simplest case, handled at the end, just add this series to the map with a new mapItem
             * 2. Series key exists, neither SearchCriteriaMapItem nor singleSeries have a mention search, just add singleSeries to the existing mapItem
             * 3. Series key exists, searchCriteriaMapItem doesn't have a mention search, but singleSeries does,
             *     add singleSeries to the existing mapItem and register the mention series
             * 4. Series key exists, searchCriteriaMapItem and singleSeries has a mention request,
             *     and they are different, need to generate a new series key and try again
             */
            let seriesKey: string = singleSeries.getSeriesKey();
            let mapValue: SearchCriteriaMapItem = this.searchCriteriaMap.get(seriesKey);

            // Case 4
            // TODO: refactor this to a function, remove this as a "case" because it's not really a case
            if (
                mapValue?.mentionRequest && singleSeries?.mentions?.mentionRequestGenerator &&
                !isEqual(mapValue?.mentionRequest, singleSeries?.mentions?.mentionRequestGenerator())
            ) {
                let iterations = 1;
                do {
                    seriesKey = singleSeries.getSeriesKey() + iterations;
                    mapValue = this.searchCriteriaMap.get(seriesKey);
                    iterations++;
                } while (mapValue && !isEqual(mapValue.mentionRequest, singleSeries.mentions?.mentionRequestGenerator()));
            }

            if (mapValue) {
                // Case 2
                mapValue.series.push(singleSeries);

                // Case 3
                if (!mapValue.mentionRequest && singleSeries.mentions?.mentionRequestGenerator) {
                    mapValue.mentionRequest = singleSeries.mentions?.mentionRequestGenerator();
                }

                continue;
            }

            // Case 1
            this.searchCriteriaMap.set(
                seriesKey,
                {
                    request: singleSeries.toSearchCriteriaModel(),
                    response: null,
                    mentionRequest: singleSeries.mentions?.mentionRequestGenerator ? singleSeries.mentions?.mentionRequestGenerator() : null,
                    series: [singleSeries]
                }
            );
        }

        if (fetchData) {
            // Now that the map is populated, fetch the data
            this.fetchDataForSeries();
        }
    }

    public handleSavedFilterConflictError(filterConflictError: any) {
        const invalidValues = filterConflictError.invalidValue;
        let type, localizedValues;
        if (invalidValues.indexOf(':') > -1) {
            type = invalidValues.split(' : ')[0];
            const values: string[] = invalidValues.split(' : ')[1]?.split(',').map(x => x.trim());
            const error = new InvalidFiltersError('Filters can not be safely applied to search', [ type ], values);
            if (type !== 'Boolean') {
                switch (type) {
                    case 'MediaType':
                        type = monitoringFiltersEnum.mediaTypes;
                        localizedValues = values.map(x => this.localizationService.transform(`MediaType.${x}`));
                        break;
                    case 'Locale':
                        type = monitoringFiltersEnum.languages;
                        localizedValues = values.map(x => this.localizationService.transform(`Language.${x}`));
                        break;
                    case 'audienceReach':
                        type = monitoringFiltersEnum.audience;
                        break;
                    case 'adValueUsd':
                        type = monitoringFiltersEnum.ave;
                        break;
                }
                error.filterTypes = [ type ];
                if (localizedValues) {
                    error.localizedInvalidValueMap = new Map<monitoringFiltersEnum, string[]>();
                    error.localizedInvalidValueMap.set(type, localizedValues);
                }
                this.conflictingSavedFilters$.next(error);
            } else if (/includeUnset/.test(invalidValues)) {
                type = monitoringFiltersEnum.sentiment;
                localizedValues = [];
                let invalidValue = invalidValues;
                if (invalidValue.match(/includeUnset=[a-z]{4}/)[0].slice(-4) === 'true') {
                    localizedValues.push(this.localizationService.transform('Sentiment.NOT_AVAILABLE'));
                }
                // remove includeUnset
                invalidValue = invalidValue.split(' ; ')[1];
                // get specific ranges
                invalidValue = invalidValue.split(' : ')[1].split(', ');
                invalidValue.forEach(x => {
                    switch (x) {
                        case 'min=0.3 max=1.0':
                            localizedValues.push(this.localizationService.transform('Sentiment.POSITIVE'));
                            break;
                        case 'min=-0.2 max=0.2':
                            localizedValues.push(this.localizationService.transform('Sentiment.NEUTRAL'));
                            break;
                        case 'min=-1.0 max=-0.3':
                            localizedValues.push(this.localizationService.transform('Sentiment.NEGATIVE'));
                            break;
                    }
                });
                error.filterTypes = [ type ];
                error.localizedInvalidValueMap = new Map<monitoringFiltersEnum, string[]>();
                error.localizedInvalidValueMap.set(type, localizedValues);
                this.conflictingSavedFilters$.next(error);
            }
        } else {
            const error = new InvalidFiltersError('Filters can not be safely applied to search', [ type ]);
            const locationError = invalidValues.match(/(?:Province|City|Continent|County|Country)=\[(?:[0-9]+(?:,\s)*)+\]/)?.[0];
            if (locationError) {
                const entityType: GeographyTypesEnum = locationError.split('=')[0] as GeographyTypesEnum;
                const entityIds = locationError.match(/[0-9]+/g);
                this.journodexService.getRegions(entityIds, entityType).subscribe(regions => {
                    localizedValues = regions.map(x => {
                        let name = this.localizationService.transform(x.displayString);
                        if (name.match(/[,\(]/)) {
                            name = name.split(/[,\(]/)[0];
                        }
                        return name;
                    });
                    error.filterTypes = [ entityType.toLowerCase() as monitoringFiltersEnum ];
                    error.localizedInvalidValueMap = new Map<monitoringFiltersEnum, string[]>();
                    error.localizedInvalidValueMap.set(entityType.toLowerCase() as monitoringFiltersEnum, localizedValues);
                    this.conflictingSavedFilters$.next(error);
                });
            }
        }
    }

    async fetchDataForSeries(refreshAll: boolean = false): Promise<void> {
        if (!this.searchCriteria) {
            return;
        }
        this.conflictingSavedFilters$.next(null);
        const searchCriteriaMapItems: SearchCriteriaMapItem[] = Array.from(this.searchCriteriaMap.values());
        for (const searchCriteriaMapItem of searchCriteriaMapItems) {
            if (!searchCriteriaMapItem.series.length) {
                continue;
            }
            // we cannot do isPreviousPeriod when date query is set to all
            if ((this.dateQuery.type === DateQueryTypeEnum.ALL) && searchCriteriaMapItem.series[0].mentions.isPreviousPeriod) {
                // we want to trigger the previousPeriodDataProvider or else
                // the chart component does not trigger previousPeriodDataProvider$ and shows empty chart
                if (searchCriteriaMapItem.series[0].previousPeriodDataProvider) {
                    searchCriteriaMapItem.series[0].previousPeriodDataProvider.next([]);
                }
                continue;
            }
            // Use sets because some series might need duplicate facets
            const requestFacets: Set<MentionSearchFacetTypesEnum> = new Set();
            const requestDateFacets: Set<DateFacetModel> = new Set();
            const requestNeoFacets: Set<NeoFacetRequestApiModel> = new Set();
            let mentionRequest: ReportSeriesMentionRequestGeneratorOutput = null;

            let atLeastOneNull: boolean = refreshAll;

            for (const singleSeries of searchCriteriaMapItem.series) {
                // Determine what facets this series needs and add them to the request
                if (singleSeries.mentions.facetGenerator) {
                    const facetsNeeded: ReportSeriesFacetGeneratorOutput = singleSeries.mentions.facetGenerator();
                    switch (facetsNeeded.identifier) {
                        case ReportSeriesFacetGeneratorEnum.facet:
                            (facetsNeeded.facet as MentionSearchFacetTypesEnum[]).forEach(facet => requestFacets.add(facet));
                            break;
                        case ReportSeriesFacetGeneratorEnum.dateFacet:
                            // not exactly needed, but seems that we keep adding even though it exists
                            const dateReq = Array.from(requestDateFacets);
                            let contains = false;
                            dateReq.forEach(req => isEqual(req, facetsNeeded.facet as DateFacetModel) ? contains = true : null);

                            if (!contains) {
                                requestDateFacets.add(facetsNeeded.facet as DateFacetModel);
                            }
                            break;
                        case ReportSeriesFacetGeneratorEnum.neoFacet:
                            // don't want to add duplicate neofacets in payload
                            const exists = some([...requestNeoFacets], (item: any) => isEqual(item, facetsNeeded.facet));
                            if (!exists) {
                                requestNeoFacets.add(facetsNeeded.facet as NeoFacetRequestApiModel);
                            }
                            break;
                    }

                    if (refreshAll) {
                        singleSeries.dataProvider.next(null);
                    } else {
                        if (singleSeries.dataProvider.value === null) {
                            atLeastOneNull = true;
                        }
                    }
                }

                // Handle mention series
                if (singleSeries.mentions.mentionRequestGenerator) {
                    if (refreshAll) {
                        singleSeries.mentionProvider?.next(null);
                    } else {
                        if (singleSeries.mentionProvider?.value === null) {
                            atLeastOneNull = true;
                        }
                    }
                    mentionRequest = singleSeries.mentions.mentionRequestGenerator();
                }
                if (!singleSeries.mentions.dataMapper && !singleSeries.mentions.mentionDataMapper && !singleSeries.mentions.asyncDataMapper) {
                    atLeastOneNull = true;
                }
            }

            if (!atLeastOneNull) {
                // We don't need to process this map item, none of the series are null, which means none need new data (and refreshAll is false)
                continue;
            }

            if (searchCriteriaMapItem.series[0].mentions.dataMapper ||
                searchCriteriaMapItem.series[0].mentions.mentionDataMapper ||
                searchCriteriaMapItem.series[0].mentions.asyncDataMapper) {
                searchCriteriaMapItem.request.facets = Array.from(requestFacets);
                searchCriteriaMapItem.request.dateFacets = Array.from(requestDateFacets);
                searchCriteriaMapItem.request.neoFacets = Array.from(requestNeoFacets);
            }

            let filters: MentionSearchCriteriaModel;

            if (this.hasFiltersToApply()) {
                filters = this.generateFilterSearch();
            } else {
                filters = null;
            }
            // an empty searchCriteriaModel is created for each series, so we need to compare whether the criteria is an empty one here
            if ((searchCriteriaMapItem.series[0].mentions.dataMapper ||
                searchCriteriaMapItem.series[0].mentions.mentionDataMapper ||
                searchCriteriaMapItem.series[0].mentions.asyncDataMapper) &&
                searchCriteriaMapItem.series[0].mentions?.filters &&
                !searchCriteriaMapItem.series[0].mentions.filters.searchCriteria.equals(this.createMentionSearchCriteriaModel())) {

                try {
                    filters = MentionSearchCriteriaModel.createFiltered(
                        searchCriteriaMapItem.series[0].mentions.filters.searchCriteria,
                        filters
                    );
                } catch {
                    // Couldn't apply the filters,  the filters the chart is asking for conflicts with the base search, can't fulfill this one
                    this.handleCreateFilteredError(searchCriteriaMapItem, refreshAll);
                    continue;
                }
            }

            let filteredSearch: MentionSearchCriteriaModel;

            if (searchCriteriaMapItem.series[0].mentions.useCombinedQuery ||
                (!searchCriteriaMapItem.series[0].mentions.dataMapper &&
                    !searchCriteriaMapItem.series[0].mentions.mentionDataMapper &&
                    !searchCriteriaMapItem.series[0].mentions.asyncDataMapper)
            ) {
                const reportMentionSearchModel = new MentionSearchCriteriaModel();
                reportMentionSearchModel.query = searchCriteriaMapItem.series[0].mentions.combinedQuery.clone();
                filteredSearch = MentionSearchCriteriaModel.createFiltered(
                    reportMentionSearchModel,
                    null,
                    false
                );
            } else {
                filteredSearch = MentionSearchCriteriaModel.createFiltered(
                    this.searchCriteria,
                    filters,
                    false
                );
                filteredSearch.showSimilar = this.duplicates.getValue() !== DuplicatesStatusEnum.hide;
            }
            // the base chart needs to have the time set from the report, not the time set from the drilldown
            if (searchCriteriaMapItem.series[0].mentions.baseDateRange) {
                if (!searchCriteriaMapItem.series[0].mentions.isPreviousPeriod) {
                    filteredSearch.query.dateQuery = searchCriteriaMapItem.series[0].mentions.baseDateRange;
                } else {
                    filteredSearch.query.dateQuery = DateQueryModel.getPreviousPeriod(searchCriteriaMapItem.series[0].mentions.baseDateRange);
                }
            } else {
                if (!searchCriteriaMapItem.series[0].mentions.isPreviousPeriod) {
                    filteredSearch.query.dateQuery = this.dateQuery;
                } else {
                    if (searchCriteriaMapItem.series[0].mentions.skipPeriod) {
                        filteredSearch.query.dateQuery = DateQueryModel.getPreviousPeriod(DateQueryModel.getPreviousPeriod(this.dateQuery));
                        if (this.dateQuery.timezone) {
                            filteredSearch.query.dateQuery.timezone = this.dateQuery.timezone;
                        }
                    } else {
                        filteredSearch.query.dateQuery = DateQueryModel.getPreviousPeriod(this.dateQuery);
                        if (this.dateQuery.timezone) {
                            filteredSearch.query.dateQuery.timezone = this.dateQuery.timezone;
                        }
                    }
                }
            }

            if (filteredSearch.filterQuery) {
                filteredSearch.filterQuery.dateQuery = filteredSearch.query.dateQuery;
            }

            if (filters) {
                filteredSearch.savedFilterIds = filters.savedFilterIds;
            }
            // Now that we have the dateQuery, we need to set up the neoFacets that have date requests with their proper range and gap values
            for (const neoFacetRequest of searchCriteriaMapItem.request.neoFacets) {
                if (!neoFacetRequest.dateField) {
                    continue;
                }

                let startISOTimeString: string = '';
                let endISOTimeString: string = '';

                switch (filteredSearch.query.dateQuery.type) {
                    case DateQueryTypeEnum.RELATIVE:
                        startISOTimeString = moment().add(filteredSearch.query.dateQuery.relativeStartOffsetH, 'hours').toISOString();
                        endISOTimeString = moment().add(filteredSearch.query.dateQuery.relativeEndOffsetH, 'hours').toISOString();
                        break;
                    case DateQueryTypeEnum.RELATIVE_TIMEZONE:
                        startISOTimeString = DateQueryModel.getStartDateForRelativeTimezone(
                            filteredSearch.query.dateQuery.relativeTimezoneDateType, filteredSearch.query.dateQuery.timezone
                        ).toISOString();
                        endISOTimeString = DateQueryModel.getEndDateForRelativeTimezone(
                            filteredSearch.query.dateQuery.relativeTimezoneDateType, filteredSearch.query.dateQuery.timezone
                        ).toISOString();
                        break;
                    case DateQueryTypeEnum.ABSOLUTE:
                        startISOTimeString = moment(filteredSearch.query.dateQuery.absoluteRange.start).toISOString();
                        endISOTimeString = moment(filteredSearch.query.dateQuery.absoluteRange.end).toISOString();
                        break;
                    case DateQueryTypeEnum.ALL:
                        startISOTimeString = null;
                        endISOTimeString = null;
                }

                if (filteredSearch.query.dateQuery.type === DateQueryTypeEnum.ALL) {
                    delete neoFacetRequest.range;
                } else {
                    neoFacetRequest.range = {
                        start: startISOTimeString,
                        end: endISOTimeString
                    };
                }

                const totalHours = DateQueryModel.getTotalHoursInQuery(filteredSearch.query.dateQuery);
                if (totalHours > 24 && filteredSearch.query.dateQuery?.relativeTimezoneDateType !== RelativeTimezoneDateTypeEnum.LAST_24_HOURS) {
                    if (filteredSearch.query.dateQuery.type !== DateQueryTypeEnum.ALL) {
                        neoFacetRequest.gap = 86400000; // 24 hours * 60 minutes * 60 seconds * 1000 milliseconds
                    } else {
                        neoFacetRequest.gap = null;
                    }
                } else {
                    neoFacetRequest.gap = 3600000; // 60 minutes * 60 seconds * 1000 milliseconds
                }
            }

            searchCriteriaMapItem.request.query = filteredSearch.query.getApiEntity();
            searchCriteriaMapItem.request.filterQuery = filteredSearch.filterQuery?.getApiEntity(true);
            searchCriteriaMapItem.request.savedFilterIds = filteredSearch.savedFilterIds;
            searchCriteriaMapItem.request.showSimilar = filteredSearch.showSimilar;
            searchCriteriaMapItem.request.requireCombinedQueryResults = true;
            searchCriteriaMapItem.request.isAdHocSearch = filteredSearch.isAdHocSearch;


            if (searchCriteriaMapItem.request.query.publicationDate && !searchCriteriaMapItem.request.query.publicationDate.timeZone) {
                searchCriteriaMapItem.request.query.publicationDate.timeZone = this.userTimezonePreference;
            }
            if (searchCriteriaMapItem.request.query.harvestedDate && !searchCriteriaMapItem.request.query.harvestedDate.timeZone) {
                searchCriteriaMapItem.request.query.harvestedDate.timeZone = this.userTimezonePreference;
            }

            if (searchCriteriaMapItem.request.filterQuery?.publicationDate && !searchCriteriaMapItem.request.filterQuery.publicationDate.timeZone) {
                searchCriteriaMapItem.request.filterQuery.publicationDate.timeZone = this.userTimezonePreference;
            }
            if (searchCriteriaMapItem.request.filterQuery?.harvestedDate && !searchCriteriaMapItem.request.filterQuery.harvestedDate.timeZone) {
                searchCriteriaMapItem.request.filterQuery.harvestedDate.timeZone = this.userTimezonePreference;
            }

            searchCriteriaMapItem.series.forEach(series => series.fullSearch = filteredSearch);

            if (mentionRequest) {
                searchCriteriaMapItem.request.pageSize = mentionRequest.pageSize;
                searchCriteriaMapItem.request.sortField = mentionRequest.sortField;
                searchCriteriaMapItem.request.sortOrder = mentionRequest.sortOrder;
                searchCriteriaMapItem.request.sortFunction = mentionRequest.sortFunction;
                searchCriteriaMapItem.request.isWordCloudRequest = mentionRequest.isWordCloudRequest;
            }

            let responseObservable: Observable<MentionsSearchResponseModel>;

            if (this.searchCriteria.isMonitorSearch) {
                responseObservable = this.mentionsApi.viSearchAccount(searchCriteriaMapItem.request);
            }  else if (this.searchCriteria.isAccountSearch) {
                responseObservable = this.mentionsApi.searchAccount(searchCriteriaMapItem.request);
            }  else {
                responseObservable = this.mentionsApi.searchMentions(searchCriteriaMapItem.request);
            }

            // When isolating social topics sometimes the Summary by Social Topic Chart topic selection hasn't updated yet, which causes an error
            if (
                !!searchCriteriaMapItem.request.filterQuery?.agent?.include
                && !!searchCriteriaMapItem.request.query.agent?.include
            ) {
                const valid = searchCriteriaMapItem.request.filterQuery?.agent?.include.every(id => {
                    return searchCriteriaMapItem.request.query.agent?.include.includes(id);
                });
                if (!valid) {
                    return;
                }
            }

            // Caching logic
            responseObservable.pipe(
                tap(
                    (response) => {
                        if (!searchCriteriaMapItem.series[0]) {
                            // The map item is no longer present, no need to continue
                            return;
                        }
                        // Add it to a cache in a tap, so we don't have to care at subscribe time if the data is live or cached
                        this.cacheService.set(
                            (
                                searchCriteriaMapItem.series[0].getSeriesKey() +
                                JSON.stringify(searchCriteriaMapItem.request.query) +
                                JSON.stringify(searchCriteriaMapItem.request.filterQuery) +
                                (searchCriteriaMapItem.mentionRequest ? JSON.stringify(searchCriteriaMapItem.mentionRequest) : '')
                            ),
                            {
                                request: searchCriteriaMapItem.request,
                                response: response,
                                mentionRequest: searchCriteriaMapItem.mentionRequest
                            }
                        );
                    }
                ),
                catchError((err: HttpErrorResponse) => {
                    if (err?.status === 400) {
                        this.handleSavedFilterConflictError(err.error?.details?.[0]);
                        return of (new MentionsSearchResponseModel());
                    } else {
                        throw err;
                    }
                })
            );

            // Check to see if the cache has it
            const cachedItem: SearchCriteriaMapItem = await this.cacheService.get(
                searchCriteriaMapItem.series[0].getSeriesKey() +
                JSON.stringify(searchCriteriaMapItem.request.query) +
                JSON.stringify(searchCriteriaMapItem.request.filterQuery) +
                (searchCriteriaMapItem.mentionRequest ? JSON.stringify(searchCriteriaMapItem.mentionRequest) : '')
            );
            if (cachedItem) {
                // The cache has something in it, does that thing fulfill all the current facets associated with this series?
                if (
                    isEqual(cachedItem.mentionRequest, searchCriteriaMapItem.mentionRequest) && // Confirms mention request details match
                    isEqual(cachedItem.request.dateFacets, searchCriteriaMapItem.request.dateFacets) &&
                    isEqual(cachedItem.request.facets, searchCriteriaMapItem.request.facets) &&
                    isEqual(JSON.parse(JSON.stringify(cachedItem.request.query)), JSON.parse(JSON.stringify(searchCriteriaMapItem.request.query))) &&
                    this.compareNeoFacets(cachedItem.request.neoFacets, searchCriteriaMapItem.request.neoFacets)
                ) {
                    // Use from and asyncScheduler because some things don't like getting data synchronously
                    responseObservable = from(of(cachedItem.response), asyncScheduler);
                }
            }
            this.handleObservers(responseObservable, searchCriteriaMapItem, refreshAll);
        }
    }

    arraysOverlap<T>(a: T[], b: T[]): boolean {
        const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
        return shorter.every((el) => longer.includes(el));
    }

    setBlankData(): void{
        const searchCriteriaMapItems: SearchCriteriaMapItem[] = Array.from(this.searchCriteriaMap.values());
        for (const searchCriteriaMapItem of searchCriteriaMapItems) {
            for (const singleSeries of searchCriteriaMapItem.series) {
                let returnValue = [{count: 0}];
                if (!searchCriteriaMapItem.series[0].mentions.dataMapper &&
                    !searchCriteriaMapItem.series[0].mentions.mentionDataMapper &&
                    !searchCriteriaMapItem.series[0].mentions.asyncDataMapper) {
                    returnValue = [];
                }
                if (singleSeries.hasPreviousPeriod && singleSeries.mentions.isPreviousPeriod) {
                    singleSeries.previousPeriodDataProvider.next(returnValue);
                } else {
                    singleSeries.dataProvider.next(returnValue);
                }

                if (singleSeries.mentions.mentionDataMapper) {
                    singleSeries.mentionProvider.next([]);
                }
            }
        }
    }

    handleObservers(responseObservable: Observable<MentionsSearchResponseModel>, searchCriteriaMapItem: SearchCriteriaMapItem, refreshAll: boolean) {
        this.pendingFetchCount++;
        this.fetchingData$.next(true);
        responseObservable.pipe(
            tap(response => searchCriteriaMapItem.response = response),
            mergeMap(response => {
                return searchCriteriaMapItem.series.map(singleSeries => {
                    if (singleSeries.appendCombineQuery) {
                        const combinedQuery = new MentionSearchQueryModel(response.combinedQuery);
                        singleSeries.fullSearch.query = combinedQuery;
                        singleSeries.fullSearch.filterQuery = null;
                        singleSeries.mentions.combinedQuery = combinedQuery;
                    }

                    if (
                        singleSeries.mentions.dataMapper &&
                        (singleSeries.dataProvider.value === null || refreshAll)
                    ) {
                        if (singleSeries.hasPreviousPeriod && singleSeries.mentions.isPreviousPeriod) {
                            singleSeries.previousPeriodDataProvider.next(
                                singleSeries.mentions.dataMapper(response, searchCriteriaMapItem.request, singleSeries)
                            );
                        } else {
                            singleSeries.dataProvider.next(singleSeries.mentions.dataMapper(response, searchCriteriaMapItem.request, singleSeries));
                        }
                    }
                    if (
                        singleSeries.mentions.mentionDataMapper &&
                        (singleSeries.mentionProvider.value === null || refreshAll)
                    ) {
                        singleSeries.mentionProvider.next(singleSeries.mentions.mentionDataMapper(response));
                    }


                    if (!singleSeries.mentions.dataMapper && !singleSeries.mentions.mentionDataMapper && !singleSeries.mentions.asyncDataMapper) {
                        this.handleReportsResponse(response, singleSeries);
                    }

                    if (
                        singleSeries.mentions.asyncDataMapper
                        && (singleSeries.dataProvider.value === null || refreshAll)
                    ) {
                        return singleSeries.mentions.asyncDataMapper(response, searchCriteriaMapItem.request, singleSeries).pipe(
                            tap(dataPoints => {
                                singleSeries.dataProvider.next(dataPoints);
                            })
                        );
                    } else {
                        return of(null);
                    }
                });
            }),
            mergeMap(x => x)
        ).subscribe({
            error: (err) => {
                console.log('err', err);
                this.handleCreateFilteredError(searchCriteriaMapItem, refreshAll);
                this.completeFetch();
            },
            complete: () => this.completeFetch()
        });
    }

    /**
     * fetchingData$ is shared across every searchCriteriaMapItem's request, so we can only
     * report "done loading" once every group in flight has finished - otherwise a fast group
     * finishing before a slower one flips loading to false while the slower one is still
     * pending, which tears down and rebuilds chart components (e.g. the MAD trend chart)
     * mid-refresh instead of once the data actually settles.
     */
    private completeFetch(): void {
        this.pendingFetchCount = Math.max(0, this.pendingFetchCount - 1);
        if (this.pendingFetchCount === 0) {
            this.fetchingData$.next(false);
        }
    }

    handleReportsResponse(response, series) {}

    /**
     * This method is used to handle the scenario that filters the chart is asking for conflicts with the base search
     * e.g. Base search is stricted to Facebook network, but it asks for twitter data in summar by social network chart
     */
    private handleCreateFilteredError(searchCriteriaMapItem: SearchCriteriaMapItem, refreshAll: boolean): void {
        const emptyResponse = new MentionsSearchResponseModel();
        for (const singleSeries of searchCriteriaMapItem.series) {
            if (
                singleSeries.mentions.dataMapper &&
                (singleSeries.dataProvider.value === null || refreshAll)
            ) {
                singleSeries.dataProvider.next(singleSeries.mentions.dataMapper(emptyResponse, searchCriteriaMapItem.request, singleSeries));
            }

            if (
                singleSeries.mentions.mentionDataMapper &&
                (singleSeries.mentionProvider.value === null || refreshAll)
            ) {
                singleSeries.mentionProvider.next(singleSeries.mentions.mentionDataMapper(emptyResponse));
            }
        }
    }

    /**
     * The map contains all the series that are currently active in the dashboard, when a series is no longer needed, it gets removed from the series so data
     * Stops being fetched for it
     *
     * NOTE: You must call deregister before you modify the series, if you modify the series and then call deregister on the modified series, the series will be
     * failed to be found, which throws an error
     */
    deregisterSeries(series?: ReportSeriesModel[]) {
        if (series && (this.searchCriteriaMap.size > 0)) {
            const mapItemsToRemove: string[] = [];
            for (const singleSeries of series) {
                if (singleSeries) {
                    const searchCriteriaMapItem: SearchCriteriaMapItem = this.searchCriteriaMap.get(singleSeries.getSeriesKey());
                    if (searchCriteriaMapItem && searchCriteriaMapItem.series.includes(singleSeries)) {
                        searchCriteriaMapItem.series.splice(searchCriteriaMapItem.series.indexOf(singleSeries), 1);
                        if (searchCriteriaMapItem.series.length === 0) {
                            mapItemsToRemove.push(singleSeries.getSeriesKey());
                        }

                        if (searchCriteriaMapItem.mentionRequest && singleSeries.mentions?.mentionRequestGenerator) {
                            // We need to remove the mention request associated with this map item iif no other series in the map item also uses it
                            const singleSeriesMentionRequest = singleSeries.mentions.mentionRequestGenerator();

                            if (!searchCriteriaMapItem.series.find(
                                mapSeries => isEqual(
                                    mapSeries.mentions?.mentionRequestGenerator ? mapSeries.mentions.mentionRequestGenerator() : null,
                                    singleSeriesMentionRequest
                                )
                            )) {
                                searchCriteriaMapItem.mentionRequest = null;
                            }
                        }
                    } else {
                        // This error is handy to keep in to warn developers if they've deregistered improperly
                        // throw new Error('The series doesn\'t belong to a map or isn\'t in it\'s map');
                        console.log('The series doesn\'t belong to a map or isn\'t in it\'s map');
                    }
                }
            }

            for (const mapItemToRemove of mapItemsToRemove) {
                this.searchCriteriaMap.delete(mapItemToRemove);
            }
        } else {
            this.searchCriteriaMap.clear();
        }
    }

    /**
     * Creates a search query from the current filters
     */
    generateFilterSearch(): MentionSearchCriteriaModel {
        return super.generateFilterSearchCriteria();
    }

    createMentionSearchCriteriaModel(): MentionSearchCriteriaModel {
        return new MentionSearchCriteriaModel();
    }

    private compareNeoFacets(a: NeoFacetRequestApiModel[], b: NeoFacetRequestApiModel[]): boolean {
        if (!!a !== !!b) {
            return false;
        }

        if (a.length !== b.length) {
            return false;
        }
        // Alphabetically sort both arrays on keys, which should be unique inside each request
        a.sort((sortA, sortB) => sortA.key < sortB.key ? -1 : sortA.key > sortB.key ? 1 : 0);
        b.sort((sortA, sortB) => sortA.key < sortB.key ? -1 : sortA.key > sortB.key ? 1 : 0);
        for (let i = 0; i < a.length; i++) {
            // Take a clone so we can safely modify them
            const cloneA: NeoFacetRequestApiModel = JSON.parse(JSON.stringify(a[i]));
            const cloneB: NeoFacetRequestApiModel = JSON.parse(JSON.stringify(b[i]));
            // Delete the range, differences in time don't matter, the cached service already keeps us from receiving old requests
            delete cloneA.range;
            delete cloneB.range;
            if (JSON.stringify(cloneA) !== JSON.stringify(cloneB)) {
                return false;
            }
        }
        return true;
    }

    getIsFetchData(): Observable<boolean> {
        return this.fetchingData$;
    }
}
